import { useOrganization, OrgRole } from "@/contexts/OrganizationContext";

/**
 * PM permission types mapped to org roles.
 * 
 * Permission matrix:
 * | Permission          | owner | admin | dispatcher | finance | readonly |
 * |---------------------|-------|-------|------------|---------|----------|
 * | view_dashboard      |  ✅   |  ✅   |    ✅      |   ✅    |    ✅    |
 * | view_properties     |  ✅   |  ✅   |    ✅      |   ❌    |    ✅    |
 * | manage_properties   |  ✅   |  ✅   |    ❌      |   ❌    |    ❌    |
 * | view_contractors    |  ✅   |  ✅   |    ✅      |   ❌    |    ✅    |
 * | manage_contractors  |  ✅   |  ✅   |    ❌      |   ❌    |    ❌    |
 * | view_jobs           |  ✅   |  ✅   |    ✅      |   ❌    |    ✅    |
 * | dispatch_jobs       |  ✅   |  ✅   |    ✅      |   ❌    |    ❌    |
 * | book_service        |  ✅   |  ✅   |    ✅      |   ❌    |    ❌    |
 * | view_disputes       |  ✅   |  ✅   |    ✅      |   ❌    |    ✅    |
 * | manage_disputes     |  ✅   |  ✅   |    ✅      |   ❌    |    ❌    |
 * | view_reports        |  ✅   |  ✅   |    ✅      |   ✅    |    ✅    |
 * | view_billing        |  ✅   |  ✅   |    ❌      |   ✅    |    ❌    |
 * | manage_billing      |  ✅   |  ✅   |    ❌      |   ✅    |    ❌    |
 * | view_settings       |  ✅   |  ✅   |    ❌      |   ❌    |    ❌    |
 * | manage_team         |  ✅   |  ✅   |    ❌      |   ❌    |    ❌    |
 * | export_data         |  ✅   |  ✅   |    ✅      |   ✅    |    ❌    |
 */

export type PMPermission =
  | "view_dashboard"
  | "view_properties"
  | "manage_properties"
  | "view_contractors"
  | "manage_contractors"
  | "view_jobs"
  | "dispatch_jobs"
  | "book_service"
  | "view_disputes"
  | "manage_disputes"
  | "view_reports"
  | "view_billing"
  | "manage_billing"
  | "view_settings"
  | "manage_team"
  | "export_data";

const PERMISSION_MAP: Record<PMPermission, OrgRole[]> = {
  view_dashboard: ["owner", "admin", "dispatcher", "finance", "readonly"],
  view_properties: ["owner", "admin", "dispatcher", "readonly"],
  manage_properties: ["owner", "admin"],
  view_contractors: ["owner", "admin", "dispatcher", "readonly"],
  manage_contractors: ["owner", "admin"],
  view_jobs: ["owner", "admin", "dispatcher", "readonly"],
  dispatch_jobs: ["owner", "admin", "dispatcher"],
  book_service: ["owner", "admin", "dispatcher"],
  view_disputes: ["owner", "admin", "dispatcher", "readonly"],
  manage_disputes: ["owner", "admin", "dispatcher"],
  view_reports: ["owner", "admin", "dispatcher", "finance", "readonly"],
  view_billing: ["owner", "admin", "finance"],
  manage_billing: ["owner", "admin", "finance"],
  view_settings: ["owner", "admin"],
  manage_team: ["owner", "admin"],
  export_data: ["owner", "admin", "dispatcher", "finance"],
};

export const useOrgPermissions = () => {
  const { currentRole, loading } = useOrganization();

  const can = (permission: PMPermission): boolean => {
    if (!currentRole) return false;
    return PERMISSION_MAP[permission].includes(currentRole);
  };

  const canAny = (...permissions: PMPermission[]): boolean => {
    return permissions.some(can);
  };

  const canAll = (...permissions: PMPermission[]): boolean => {
    return permissions.every(can);
  };

  return {
    can,
    canAny,
    canAll,
    currentRole,
    loading,
  };
};
